import type {
  GrantOrigin,
  GrantScope,
  Mechanism,
  PrincipalType,
  ReviewDecision,
  TrustLapseReason,
  TrustStatus,
} from './types';
import { isTrustWindowOpen } from './lib/review-date';
import type { TFunction } from 'i18next';

// Display-only "status" maps to the design-system Badge color palette.
// Shared here so the main table, External access table, and the grant
// slide-over agree on a single source of truth.
export type BadgeStatus = 'good' | 'warning' | 'bad' | 'info' | 'neutral';

export const scopeLabels = (
  t: TFunction<'accessGrants'>,
): Record<GrantScope, string> => ({
  public: t('public'),
  'cross-org': t('crossOrg'),
  federated: t('federated'),
  'same-org': t('sameOrg'),
  'same-account': t('sameAccount'),
  'aws-service': t('awsService'),
});

export const scopeDescriptions = (
  t: TFunction<'accessGrants'>,
): Record<GrantScope, string> => ({
  public: t('openToEveryoneThePrincipalIsA'),
  'cross-org': t('aSpecificAwsAccountThatIsOutside'),
  federated: t('aFederatedIdentityAPrincipalThatAuthenticates'),
  'same-org': t('aPrincipalWithinTheSameAwsOrganisation'),
  'same-account': t('aPrincipalWithinTheSameAwsAccount'),
  'aws-service': t('anAwsServiceEGLambdaS3'),
});

export const trustLabels = (
  t: TFunction<'accessGrants'>,
): Record<TrustStatus, string> => ({
  trusted: t('trusted'),
  untrusted: t('untrusted'),
});

export const originLabels = (
  t: TFunction<'accessGrants'>,
): Record<GrantOrigin, string> => ({
  ext: t('external'),
  int: t('internal'),
});

export const originDescriptions = (
  t: TFunction<'accessGrants'>,
): Record<GrantOrigin, string> => ({
  ext: t('thePrincipalIsOutsideThisAwsAccount'),
  int: t('thePrincipalIsWithinTheSameAws'),
});

// Copy for the trustStatus filter facet, which slices on the coarse status only.
// It stays deliberately vague about WHY a grant is trusted: the facet selects
// both trust sources at once. The per-state wording in trustStateDisplay(t) is
// what a single grant's badge shows.
export const trustDescriptions = (
  t: TFunction<'accessGrants'>,
): Record<NonNullable<TrustStatus> | 'unclassified', string> => ({
  trusted: t('thisAccessIsTrustedAndRaisesNo'),
  untrusted: t('thisAccessIsNotTrustedAndRaises'),
  unclassified: t('trustStatusHasNotBeenDetermined'),
});

// Why a reviewer's trust is not in force. 'content_changed' is the one that needs
// a human: the grant was widened after it was trusted, so trust ended early rather
// than simply running out.
export const trustLapseReasonLabels = (
  t: TFunction<'accessGrants'>,
): Record<TrustLapseReason, string> => ({
  expired: t('reviewDatePassed'),
  date_cleared: t('reviewDateCleared'),
  content_changed: t('permissionsChangedAfterItWasTrusted'),
});

export const mechanismLabels = (
  t: TFunction<'accessGrants'>,
): Record<Mechanism, string> => ({
  resource_policy: t('resourcePolicy'),
  trust_policy: t('trustPolicy'),
  ram_share: t('ramShare'),
  kms_grant: t('kmsGrant'),
  snapshot_share: t('snapshotShare'),
  attribute_share: t('attributeShare'),
  ses_authz: t('sesAuthorization'),
  cognito_user_pool: t('cognitoUserPool'),
});

export const mechanismDescriptions = (
  t: TFunction<'accessGrants'>,
): Record<Mechanism, string> => ({
  resource_policy: t('policyAttachedDirectlyToTheResourceThat'),
  trust_policy: t('iamRoleTrustPolicyAllowsExternalPrincipals'),
  ram_share: t('awsResourceAccessManagerShareGrantsCross'),
  kms_grant: t('kmsGrantAllowsAGranteeToPerform'),
  snapshot_share: t('snapshotSharedDirectlyWithAnExternalAws'),
  attribute_share: t('resourceSharedCrossAccountViaAPermission'),
  ses_authz: t('sesSendingAuthorizationPolicyAllowsADelegated'),
  cognito_user_pool: t('cognitoUserPoolConfiguredForExternalIdentity'),
});

export const reviewDecisionLabels = (
  t: TFunction<'accessGrants'>,
): Record<ReviewDecision, string> => ({
  keep: t('keep'),
  remove: t('common:remove'),
  review_later: t('reviewLater'),
  trust_until_review: t('trustUntilReview'),
});

// Canonical display order for the decision options, shared by the inline table
// cell and the slide-over Audit tab (each renders its own empty "—" option).
// 'trust_until_review' is last because it is the only one that needs a date
// collected before it can be saved.
export const REVIEW_DECISIONS: ReviewDecision[] = [
  'keep',
  'remove',
  'review_later',
  'trust_until_review',
];

export const FEDERATED_PRINCIPAL_TYPES = [
  'saml',
  'oidc',
  'external_idp',
] as const satisfies readonly PrincipalType[];

export function isFederatedPrincipalType(type: PrincipalType): boolean {
  return (FEDERATED_PRINCIPAL_TYPES as readonly PrincipalType[]).includes(type);
}

export const principalTypeLabels = (
  t: TFunction<'accessGrants'>,
): Record<PrincipalType, string> => ({
  aws_account: t('awsAccount'),
  aws_role: t('awsRole'),
  aws_user: t('awsUser'),
  // A wildcard `*` principal is not a distinct principal "type" — it reads as
  // "Any". A `*` narrowed by a condition is "Any (conditional)". The Scope column
  // still distinguishes truly-public exposure from a narrowed one.
  public: t('any'),
  conditional: t('anyConditional'),
  aws_org: t('awsOrganization'),
  oidc: 'OIDC',
  saml: 'SAML',
  cognito_identity: t('cognitoIdentityPool'),
  canonical_user: t('canonicalUser'),
  external_idp: t('externalIdP'),
  aws_service: t('awsService'),
  aws_support: t('awsSupport'),
});

// Display data for the Scope, Origin, and Trust badges. Co-locating label
// and badge status here lets both /access-grants and CIEM External access
// render the same colors without each carrying its own copy of these maps.
export const scopeDisplay = (
  t: TFunction<'accessGrants'>,
): Record<GrantScope, { label: string; status: BadgeStatus }> => ({
  public: { label: scopeLabels(t).public, status: 'bad' },
  'cross-org': { label: scopeLabels(t)['cross-org'], status: 'warning' },
  federated: { label: scopeLabels(t).federated, status: 'warning' },
  'same-org': { label: scopeLabels(t)['same-org'], status: 'info' },
  'same-account': { label: scopeLabels(t)['same-account'], status: 'neutral' },
  'aws-service': { label: scopeLabels(t)['aws-service'], status: 'neutral' },
});

export const originDisplay = (
  t: TFunction<'accessGrants'>,
): Record<GrantOrigin, { label: string; status: BadgeStatus }> => ({
  ext: { label: originLabels(t).ext, status: 'warning' },
  int: { label: originLabels(t).int, status: 'neutral' },
});

// Trust has three display states — `trusted` / `untrusted` from the API,
// plus an `unclassified` key for the null case. Consumers should look up
// `trustDisplay(t)[trustStatus ?? 'unclassified']` to handle all three.
//
// This is the coarse status only, and is what the filter facet and the other
// modules' grant tables render. A grant's own badge should use resolveTrustState
// below, which tells the two trust sources apart.
export const trustDisplay = (
  t: TFunction<'accessGrants'>,
): Record<
  NonNullable<TrustStatus> | 'unclassified',
  { label: string; status: BadgeStatus }
> => ({
  trusted: { label: trustLabels(t).trusted, status: 'good' },
  untrusted: { label: trustLabels(t).untrusted, status: 'bad' },
  unclassified: { label: t('unclassified'), status: 'neutral' },
});

/**
 * The trust states a single grant can be in. `trustStatus` alone cannot separate
 * these: the two trust sources — the principal being on the Trusted Principals
 * list, and a reviewer's time-boxed decision — both land on `trusted`, and the
 * lapse that means "someone widened this after it was trusted" is just another
 * `untrusted`.
 */
export type TrustState =
  | 'trusted-principal'
  | 'trusted-until'
  | 'needs-re-review'
  | 'untrusted'
  | 'unclassified';

// The grant fields the state is derived from.
type TrustFields = {
  trustStatus: TrustStatus | null;
  trustedUntil: string | null;
  trustLapseReason: TrustLapseReason | null;
};

export function resolveTrustState(grant: TrustFields): TrustState {
  // The open window is read FIRST, and deliberately not gated on trustStatus.
  // A review write cannot set trust_status — the column is derived, and only the
  // scan writes it — so it lags a reviewer's decision by up to a scan interval.
  // Trusting a grant and then reading trustStatus would render the row the
  // reviewer just trusted as a red "Untrusted" until the next scan caught up.
  if (isTrustWindowOpen(grant.trustedUntil)) {
    return 'trusted-until';
  }
  // Past that the window is shut, so trustStatus is the only word on whether
  // anything else trusts this grant — the principal being on the trusted list.
  // Between a window ending and the next scan this reads as plain "Trusted"
  // rather than naming a date that has already passed, which is also honest
  // about the effect: the finding really is still suppressed until that scan.
  if (grant.trustStatus === 'trusted') {
    return 'trusted-principal';
  }
  // The grant's permissions changed after a reviewer trusted them, so the
  // decision answered a question about access that no longer exists. The backend
  // has put the grant back to undecided; this reason is the only thing left
  // saying why it needs a fresh look.
  if (grant.trustLapseReason === 'content_changed') {
    return 'needs-re-review';
  }
  return grant.trustStatus === null ? 'unclassified' : 'untrusted';
}

export const trustStateDisplay = (
  t: TFunction<'accessGrants'>,
): Record<
  TrustState,
  { label: string; status: BadgeStatus; description: string }
> => ({
  'trusted-principal': {
    label: trustLabels(t).trusted,
    status: 'good',
    description: t('thePrincipalIsOnYourTrustedPrincipals'),
  },
  'trusted-until': {
    label: trustLabels(t).trusted,
    status: 'good',
    description: t('aReviewerTrustedThisAccessUntilIts'),
  },
  'needs-re-review': {
    label: t('needsReReview'),
    status: 'warning',
    description: t('thePermissionsChangedAfterThisAccessWas'),
  },
  untrusted: {
    label: trustLabels(t).untrusted,
    status: 'bad',
    description: t('thisAccessIsNeitherOnYourTrusted'),
  },
  unclassified: {
    label: t('unclassified'),
    status: 'neutral',
    description: t('trustStatusHasNotBeenDetermined'),
  },
});

// Tooltip copy for a grant's trust badge. On an untrusted grant that WAS trusted
// once, the lapse reason is named — otherwise a trust that simply ran out reads
// identically to access nobody ever approved.
export function trustStateDescription(
  t: TFunction<'accessGrants'>,
  grant: TrustFields,
): string {
  const state = resolveTrustState(grant);
  const { description } = trustStateDisplay(t)[state];
  if (state === 'untrusted' && grant.trustLapseReason) {
    return `${description} ${t('aReviewerHadTrustedIt', {
      reason: trustLapseReasonLabels(t)[grant.trustLapseReason].toLowerCase(),
    })}`;
  }
  return description;
}
